import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  ViewChild,
  inject,
  input,
} from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { CandidateSelectOption } from '@core/search-types';
import {
  AvatarComponent,
  ButtonComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  EmptyStateComponent,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  RtStatefulComponent,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { CandidateSelectDropdownStrategy } from '../../types/candidate-select-dropdown-strategy';

type State = {
  query: string;
  loading: boolean;
  candidates: CandidateSelectOption[];
};

@Component({
  selector: 'rt-candidate-select-dropdown',
  templateUrl: './candidate-select-dropdown.component.html',
  styleUrls: ['./candidate-select-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    FormFieldComponent,
    InputComponent,
    IconComponent,
    SpinnerComponent,
    DropdownItemComponent,
    AvatarComponent,
    TruncateComponent,
    EmptyStateComponent,
    ButtonComponent,
    TranslatePipe,
    DropdownHeaderComponent,
  ],
})
export class CandidateSelectDropdownComponent extends RtStatefulComponent<State> {
  private readonly i18n = inject(I18nService);
  private readonly candidateSelectDropdownStrategy = inject(CandidateSelectDropdownStrategy);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly selected = input<CandidateSelectOption[]>([]);
  public readonly dropdownTitle = input<string>(
    this.i18n.translate('app.assignments.candidates.select_candidate'),
  );
  public readonly emptyStateHeading = input<string>();
  public readonly emptyStateSubheading = input<string>();
  public readonly dropdownWidth = input<DropdownWidth>(DropdownWidth.Medium);
  public readonly defaultQuery = input<string>();
  @Input() public header?: string;

  public selectCandidate = output<CandidateSelectOption>();
  public close = output<void>();

  @ViewChild('queryInput', { static: true }) private queryInput: InputComponent;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      query: '',
      loading: false,
      candidates: [],
    });
  }

  public ngOnInit(): void {
    this.state$
      .pipe(
        map(state => state.query),
        distinctUntilChanged(),
        filter(query => !!query),
        debounceTime(300),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(query => {
        this.searchCandidates(query);
      });

    this.candidateSelectDropdownStrategy.candidates
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(candidates => {
        this.setState({
          ...this.state,
          loading: false,
          candidates: this.state.query
            ? candidates.filter(c => !this.selected().find(s => s.id === c.id))
            : [],
        });
      });

    const defaultQuery = this.defaultQuery();
    if (defaultQuery?.length) {
      this.setState({ ...this.state, query: defaultQuery });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public changeQuery(query: string): void {
    this.setState({ ...this.state, query, loading: true });
  }

  public reset(): void {
    this.setState({
      ...this.state,
      query: '',
      loading: false,
      candidates: [],
    });
    this.queryInput.setFocus();
  }

  public trackByCandidate(index: number, candidate: CandidateSelectOption): number {
    return candidate.id;
  }

  private searchCandidates(query: string): void {
    this.setState({
      ...this.state,
      loading: true,
    });

    this.candidateSelectDropdownStrategy.fetch(query, this.selected().length);
  }
}
